import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import threadApi, { initializeThreadApi } from "../../api/threadApi";
import { useFightThreadUpdates } from "../../hooks/notifications/useFightThreadUpdates";

/**
 * Fight Discussion Icon Component
 * Entry point for accessing fight discussions
 * Requirements: 1.1, 7.2, 7.4
 *
 * @param {Object} props - Component props
 * @param {number} props.fightId - The fight ID to access discussion for
 * @param {boolean} props.showMessageCount - Whether to show message count badge
 * @param {string} props.size - Icon size: 'small', 'medium', 'large'
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onError - Optional error handler callback
 * @param {Function} props.onSuccess - Optional success handler callback
 */
const FightDiscussionIcon = ({
  fightId,
  showMessageCount = false,
  size = "medium",
  className = "",
  onError,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialMessageCount, setInitialMessageCount] = useState(null);

  // Use real-time message count updates
  const { messageCount, hasUpdates } = useFightThreadUpdates(
    fightId,
    initialMessageCount
  );

  // Initialize ThreadAPI with Auth0 function
  useEffect(() => {
    if (getAccessTokenSilently) {
      initializeThreadApi(getAccessTokenSilently);
    }
  }, [getAccessTokenSilently]);

  // Size configurations
  const sizeConfig = {
    small: {
      icon: "w-4 h-4",
      container: "p-1",
      badge: "text-xs min-w-[16px] h-4",
    },
    medium: {
      icon: "w-5 h-5",
      container: "p-2",
      badge: "text-xs min-w-[18px] h-5",
    },
    large: {
      icon: "w-6 h-6",
      container: "p-3",
      badge: "text-sm min-w-[20px] h-6",
    },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  /**
   * Handle fight discussion access
   * Implements auto-creation and navigation logic
   */
  const handleDiscussionClick = useCallback(
    async (event) => {
      // Prevent event bubbling to parent elements
      event.preventDefault();
      event.stopPropagation();

      // Validate fightId
      if (!fightId || typeof fightId !== "number") {
        const errorMsg = "Invalid fight ID provided";
        setError(errorMsg);
        onError?.(new Error(errorMsg));
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Access fight thread (auto-creates if needed)
        const response = await threadApi.getFightThread(fightId);

        // Update message count if available
        if (response.thread?.messageCount !== undefined) {
          setInitialMessageCount(response.thread.messageCount);
        }

        // Navigate to thread view
        const threadId = response.thread.id;
        navigate(`/threads/${threadId}`, {
          state: {
            fightId,
            threadData: response,
            fromFightCard: true,
          },
        });

        // Call success callback
        onSuccess?.(response);
      } catch (err) {
        // Handle different error types
        let errorMessage = "Failed to access fight discussion";

        if (err.type === "AUTH_ERROR") {
          errorMessage = "Please log in to access fight discussions";
        } else if (err.type === "NOT_FOUND_ERROR") {
          errorMessage = "Fight discussion not available";
        } else if (err.type === "NETWORK_ERROR") {
          errorMessage = "Connection error - please try again";
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    },
    [fightId, navigate, onError, onSuccess]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Base button classes
  const baseClasses = `
    relative inline-flex items-center justify-center
    rounded-full transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    ${config.container}
  `;

  // State-dependent classes
  const stateClasses = loading
    ? "bg-gray-100 text-gray-400 cursor-wait"
    : error
    ? "bg-red-50 text-red-600 hover:bg-red-100"
    : "bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100";

  return (
    <div className="relative inline-block">
      {/* Main Discussion Button */}
      <button
        onClick={handleDiscussionClick}
        disabled={loading}
        className={`${baseClasses} ${stateClasses} ${className}`}
        title={
          error
            ? `Error: ${error}`
            : loading
            ? "Loading discussion..."
            : "Open fight discussion"
        }
        aria-label={
          error
            ? `Fight discussion error: ${error}`
            : loading
            ? "Loading fight discussion"
            : "Open fight discussion"
        }
      >
        {/* Loading Spinner */}
        {loading && (
          <div className={`animate-spin ${config.icon}`}>
            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}

        {/* Error Icon */}
        {error && !loading && (
          <svg
            className={`${config.icon}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        )}

        {/* Discussion Icon */}
        {!loading && !error && (
          <svg
            className={`${config.icon}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        )}

        {/* Message Count Badge */}
        {showMessageCount &&
          messageCount !== null &&
          messageCount > 0 &&
          !loading && (
            <span
              className={`
              absolute -top-1 -right-1 
              ${
                hasUpdates ? "bg-green-600 animate-pulse" : "bg-blue-600"
              } text-white rounded-full
              flex items-center justify-center
              font-medium leading-none
              ${config.badge}
            `}
              aria-label={`${messageCount} messages${
                hasUpdates ? " (updated)" : ""
              }`}
            >
              {messageCount > 99 ? "99+" : messageCount}
            </span>
          )}
      </button>

      {/* Error Tooltip */}
      {error && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50">
          <div className="bg-red-600 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
            {error}
            <button
              onClick={clearError}
              className="ml-2 text-red-200 hover:text-white"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-600"></div>
        </div>
      )}
    </div>
  );
};

export default FightDiscussionIcon;
